import React from 'react';
import {
  Box, SimpleGrid, Text, Heading, Table, Thead, Tbody, Tr, Th, Td, Badge, VStack, HStack,
} from '@chakra-ui/react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, LabelList, ScatterChart, Scatter,
} from 'recharts';
import { DashboardAnalytics, HeatMapData } from '../types';
import { useLanguage } from '../hooks/useLanguage';
import HeatMap from './HeatMap';
import { getRiskLevelStyle, LEVEL_COLORS } from '../utils/riskLevel';

interface Props {
  data: DashboardAnalytics;
  heatMap: HeatMapData | null;
  showDepartmentCharts?: boolean;
}

const categoryColor = (name: string): string => {
  if (name.includes('ดำเนินงาน')) return '#2ECC71';
  if (name.includes('ปฏิบัติตามกฎ')) return '#3498DB';
  if (name.includes('การเงิน')) return '#F1C40F';
  if (name.includes('ทุจริต')) return '#7F3F3F';
  if (name.includes('ชื่อเสียง')) return '#8E44AD';
  if (name.includes('กลยุทธ์') || name.includes('ยุทธศาสตร์')) return '#2C3E50';
  if (name.includes('สาธารณ')) return '#E67E22';
  return '#95A5A6';
};

const levelColor = (l: number, i: number): { bg: string; text: string } => {
  return getRiskLevelStyle(l, i);
};

const deptDominantColor = (name: string, deptInherent: Array<{ name: string; low: number; medium: number; high: number; very_high: number }>): string => {
  const d = deptInherent.find((x) => x.name === name);
  if (!d) return LEVEL_COLORS.low.bg;
  const levels: Array<[number, string]> = [
    [d.very_high, LEVEL_COLORS.veryHigh.bg],
    [d.high, LEVEL_COLORS.high.bg],
    [d.medium, LEVEL_COLORS.medium.bg],
    [d.low, LEVEL_COLORS.low.bg],
  ];
  return levels.reduce((a, b) => (b[0] > a[0] ? b : a))[1];
};

const ChartCard: React.FC<{ title: string; children: React.ReactNode; height?: number }> = ({ title, children, height = 320 }) => (
  <Box bg="white" p={5} borderRadius="lg" boxShadow="sm">
    <Heading size="sm" mb={4} color="gray.700">{title}</Heading>
    <Box height={`${height}px`}>{children}</Box>
  </Box>
);

const PowerBiDashboard: React.FC<Props> = ({ data, heatMap, showDepartmentCharts = true }) => {
  const { t } = useLanguage();
  const { summary, risk_category, department_totals, department_inherent, department_avg, residual_completeness, avg_controls_by_dept, risk_details } = data;
  const total = summary.total_risks;
  const totalAssessed = residual_completeness.assessed;

  const catTotal = risk_category.reduce((s, c) => s + c.count, 0);
  const deptBarHeight = Math.max(260, department_totals.length * 36 + 60);
  const stackBarHeight = Math.max(260, department_inherent.length * 36 + 60);
  const controlsBarHeight = Math.max(260, avg_controls_by_dept.length * 36 + 60);

  const overallAvgResidual = totalAssessed
    ? summary.avg_residual
    : (department_avg.filter((d) => d.avg_residual != null).reduce((s, d) => s + (d.avg_residual || 0), 0) /
        Math.max(1, department_avg.filter((d) => d.avg_residual != null).length));

  const categoryPie = (
    <Box bg="white" p={5} borderRadius="lg" boxShadow="sm">
      <Heading size="sm" mb={4} color="gray.700">{t('pb.byCategory')}</Heading>
      <Box height="280px">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={risk_category} dataKey="count" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
              {risk_category.map((c) => (
                <Cell key={c.name} fill={categoryColor(c.name)} />
              ))}
            </Pie>
            <Tooltip formatter={(v: any, n: any) => [`${v} ${t('common.items', { n: '' }).replace('{n}', '').trim()}`, n]} />
          </PieChart>
        </ResponsiveContainer>
      </Box>
      <HStack mt={3} flexWrap="wrap" justify="center" spacing={3} maxH="170px" overflowY="auto">
        {risk_category.map((c) => (
          <HStack key={c.name} spacing={1} fontSize="xs">
            <Box w={3} h={3} borderRadius="full" bg={categoryColor(c.name)} />
            <Text color="gray.600">{c.name} ({((c.count / catTotal) * 100).toFixed(2)}% | {c.count})</Text>
          </HStack>
        ))}
      </HStack>
    </Box>
  );

  const residualHeatMapBox = (
    <Box bg="white" p={5} borderRadius="lg" boxShadow="sm">
      <Heading size="sm" mb={4} color="gray.700">{t('pb.residualHeatMap')}</Heading>
      {heatMap ? <HeatMap data={heatMap} matrixKey="residual" /> : <Text color="gray.500">{t('heatmap.noData')}</Text>}
    </Box>
  );

  const riskDetailsBox = (
    <Box bg="white" p={5} borderRadius="lg" boxShadow="sm">
      <Heading size="sm" mb={4} color="gray.700">{t('pb.riskDetails')}</Heading>
      <Box overflowX="auto" maxH={controlsBarHeight} overflowY="auto">
        <Table size="sm" variant="striped">
          <Thead>
            <Tr>
              <Th>{t('pb.riskCode')}</Th>
              <Th>{t('pb.riskName')}</Th>
              <Th isNumeric>{t('pb.inherentRisk')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {risk_details.map((r) => {
              const c = levelColor(r.inherent_likelihood, r.inherent_impact);
              return (
                <Tr key={r.risk_id}>
                  <Td fontSize="xs" fontWeight="bold">{r.risk_code}</Td>
                  <Td fontSize="xs">{r.risk_name}</Td>
                  <Td isNumeric>
                    <Badge bg={c.bg} color={c.text} px={2} py={1} borderRadius="md">{r.inherent_risk}</Badge>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );

  if (!showDepartmentCharts) {
    return (
      <VStack spacing={8} align="stretch">
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          {categoryPie}
          {riskDetailsBox}
        </SimpleGrid>
        {residualHeatMapBox}
      </VStack>
    );
  }

  return (
    <VStack spacing={8} align="stretch">
      {/* ═══ EXECUTIVE OVERVIEW ═══ */}
      <Box>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          {categoryPie}

          <ChartCard title={t('pb.byDepartment')} height={deptBarHeight}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={department_totals} layout="vertical" margin={{ left: 8, right: 40 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={230} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v: any) => [`${v} ${t('common.items', { n: '' }).replace('{n}', '').trim()}`, t('dashboard.totalRisks')]} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {department_totals.map((d) => (
                    <Cell key={d.name} fill={deptDominantColor(d.name, department_inherent)} />
                  ))}
                  <LabelList dataKey="count" position="right" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </SimpleGrid>

        <Box mt={6}>
          <ChartCard title={t('pb.byDepartmentInherent')} height={stackBarHeight}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={department_inherent} layout="vertical" margin={{ left: 8, right: 24 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={230} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="low" name={t('level.low')} stackId="a" fill={LEVEL_COLORS.low.bg} />
                <Bar dataKey="medium" name={t('level.medium')} stackId="a" fill={LEVEL_COLORS.medium.bg} />
                <Bar dataKey="high" name={t('level.high')} stackId="a" fill={LEVEL_COLORS.high.bg} />
                <Bar dataKey="very_high" name={t('level.veryHigh')} stackId="a" fill={LEVEL_COLORS.veryHigh.bg} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Box>
      </Box>

      {/* ═══ DEPARTMENT ANALYSIS ═══ */}
      <Box>
        <Heading size="md" color="#0F2C59" mb={4}>{t('pb.deptAnalysis')}</Heading>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          <ChartCard title={t('pb.avgInherentVsResidual')}>
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 10, right: 30, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" dataKey="avg_inherent" name="Avg Inherent" domain={[0, 'auto']} tickFormatter={(v: any) => Number(v).toFixed(1)} />
                <YAxis type="number" dataKey="avg_residual" name="Avg Residual" domain={[0, 'auto']} tickFormatter={(v: any) => Number(v).toFixed(1)} />
                <Tooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  formatter={(v: any, n: any) => [Number(v).toFixed(2), n]}
                  labelFormatter={(label: any, payload: any) => (payload && payload[0] ? payload[0].payload.name : '')}
                />
                <Scatter
                  name={t('common.department')}
                  data={department_avg.filter((d) => d.avg_residual != null).map((d) => ({ ...d, avg_residual: d.avg_residual || 0 }))}
                  fill="#0F2C59"
                />
              </ScatterChart>
            </ResponsiveContainer>
          </ChartCard>

          <Box bg="white" p={5} borderRadius="lg" boxShadow="sm">
            <HStack justifyContent="space-between" mb={4}>
              <Heading size="sm" color="gray.700">{t('pb.deptSummary')}</Heading>
              <Badge colorScheme="purple" fontSize="sm" p={2} borderRadius="md">
                {t('pb.residualCompleteness', { p: residual_completeness.percent, a: totalAssessed, t: total })}
              </Badge>
            </HStack>
            <Box overflowX="auto" maxH="280px" overflowY="auto">
              <Table size="sm" variant="striped">
                <Thead>
                  <Tr>
                    <Th>{t('common.department')}</Th>
                    <Th isNumeric>{t('pb.avgInherent')}</Th>
                    <Th isNumeric>{t('pb.avgResidual')}</Th>
                    <Th isNumeric>{t('pb.totalRisks')}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {department_avg.map((d) => (
                    <Tr key={d.name}>
                      <Td fontSize="xs">{d.name}</Td>
                      <Td isNumeric fontSize="xs">{d.avg_inherent.toFixed(2)}</Td>
                      <Td isNumeric fontSize="xs">{d.avg_residual != null ? d.avg_residual.toFixed(2) : '-'}</Td>
                      <Td isNumeric fontSize="xs">{d.total_risks}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
            <HStack mt={3} fontWeight="bold" fontSize="sm" justifyContent="space-between" px={3}>
              <Text>{t('pb.total')}</Text>
              <Text>{summary.avg_inherent.toFixed(2)} | {Number(overallAvgResidual).toFixed(2)} | {total}</Text>
            </HStack>
          </Box>
        </SimpleGrid>
      </Box>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {residualHeatMapBox}
        {riskDetailsBox}
      </SimpleGrid>

      <ChartCard title={t('pb.avgControls')} height={controlsBarHeight}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={avg_controls_by_dept} layout="vertical" margin={{ left: 8, right: 40 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" allowDecimals />
            <YAxis type="category" dataKey="name" width={230} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v: any) => [Number(v).toFixed(1), 'Avg Controls']} />
            <Bar dataKey="avg_controls" fill="#2ECC71" radius={[0, 4, 4, 0]}>
              <LabelList dataKey="avg_controls" position="right" formatter={(v: any) => Number(v).toFixed(1)} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </VStack>
  );
};

export default PowerBiDashboard;
